import React from "react";
import { Button } from "@src/lib/ui/Button";
import { Dialog, DialogContent } from "@src/lib/ui/Dialog";
import LanguageIcon from "@src/lib/icons/18px/language";
import PenOutlineIcon from "@src/lib/icons/18px/pen-outline";
import Phone2Icon from "@src/lib/icons/18px/phone-2";
import { IconProps } from "@src/lib/icons/types";
import Text from "@src/lib/ui/Text";
import OpusDocumentIllustration from "@src/lib/illustrations/OpusDocumentIllustration";
import { cn } from "@src/lib/utils/cn";
import PdfDocumentIllustration from "@src/lib/illustrations/PdfDocumentIllustration";
import { gql } from "@apollo/client";
import { useConvertPdfToOpusDocDialog_CreateTrainingResourceMutation } from "@src/features/shared/ConvertPdfToOpusDocDialog.generated";
import { useRouter } from "next/router";
import { addErrorToast } from "@src/features/app/utils/toast";
import { useFileListUpload } from "@src/features/contentDetail/hooks/useFileListUpload";
import { S3Upload as FileListUploadMedia } from "@src/features/app/hooks/useS3Upload";
import { UploadedTrainingResourceFragment } from "@src/features/app/dialogs/bulkUploadTrainingResourceDialog/BulkUploadTrainingResourceDialog.generated";
import Spinner from "@src/lib/ui/Spinner";
import {
  CreateTrainingResourceInput,
  TrainingResourceVersionType,
} from "@src/types.generated";

type CardOption = "opus-doc" | "pdf";

interface Props {
  pdfFile: File;
  onClose: () => void;
  onTrainingResourceCreated?: (
    trainingResource: UploadedTrainingResourceFragment,
  ) => void;
}

const ConvertPdfToOpusDocDialog: React.FC<Props> = (props) => {
  const router = useRouter();

  const [selectedCardOption, setSelectedCardOption] =
    React.useState<CardOption>("opus-doc");
  const [media, setMedia] = React.useState<FileListUploadMedia | null>(null);

  const [uploadPdfFile, { loading: fileListLoading }] = useFileListUpload(
    (media: FileListUploadMedia[]) => {
      if (media.length === 0) {
        // Message is intentional.
        // User is not aware of any "upload".
        // User has just selected a PDF file.
        addErrorToast({
          message: "Unable to select file. Please try again.",
          callsite: "ConvertPdfToOpusDocDialog",
        });
        return;
      }
      setMedia(media[0]);
    },
  );

  const [createTrainingResource, { loading: createTrainingResourceLoading }] =
    useConvertPdfToOpusDocDialog_CreateTrainingResourceMutation({
      onCompleted(data) {
        if (
          data.createTrainingResourceV2.success &&
          data.createTrainingResourceV2.trainingResource
        ) {
          if (props.onTrainingResourceCreated) {
            props.onTrainingResourceCreated(
              data.createTrainingResourceV2.trainingResource,
            );
            props.onClose();
            return;
          }

          router.push({
            pathname: "/library/library-item/[id]",
            query: {
              id: data.createTrainingResourceV2.trainingResource.libraryItem
                ?.id,
            },
          });
          props.onClose();
        } else {
          addErrorToast({
            message: "Unable to convert PDF. Please try again.",
            callsite: "ConvertPdfToOpusDocDialog",
          });
        }
      },
    });

  const handleOnSave = React.useCallback(async () => {
    const input: CreateTrainingResourceInput =
      selectedCardOption === "opus-doc"
        ? {
            name: {
              en: props.pdfFile?.name || "",
            },
            mediaInput: {
              mediaUrls: {
                en: media?.url || "",
              },
            },
            type: TrainingResourceVersionType.RichTextDocument,
          }
        : {
            name: {
              en: props.pdfFile?.name || "",
            },
            mediaInput: {
              mediaUrls: {
                en: media?.url || "",
              },
            },
            type: TrainingResourceVersionType.Media,
          };
    const res = await createTrainingResource({
      variables: {
        input,
      },
    });

    if (
      res.data?.createTrainingResourceV2.success &&
      res.data?.createTrainingResourceV2.trainingResource?.libraryItem?.id
    ) {
      router.push({
        pathname: "/library/library-item/[id]",
        query: {
          id: res.data?.createTrainingResourceV2.trainingResource?.libraryItem
            ?.id,
        },
      });
    }
    props.onClose();
  }, [createTrainingResource, media?.url, props, router, selectedCardOption]);

  const saveButtonText = React.useMemo(() => {
    return selectedCardOption === "opus-doc"
      ? "Convert to Opus Doc"
      : "Upload and Preview";
  }, [selectedCardOption]);

  React.useEffect(() => {
    uploadPdfFile([props.pdfFile]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Dialog open onOpenChange={props.onClose}>
      <DialogContent className="min-w-[720px] gap-0 p-0">
        {fileListLoading ? (
          <div className="flex h-[420px] flex-col items-center justify-center gap-4 self-stretch p-3">
            <Spinner size={48} />
            <Text variant="compact-title" className="text-muted-foreground">
              Uploading document
            </Text>
          </div>
        ) : (
          <>
            <div className="flex flex-col items-center justify-center border-b border-input p-4">
              <Text variant="compact-title" multiline>
                Do you want to convert this file into an Opus Document?
              </Text>
            </div>
            <div className="flex flex-1 items-center justify-center gap-3 self-stretch p-3">
              <OpusDocCardOption
                selected={selectedCardOption === "opus-doc"}
                onClick={() => setSelectedCardOption("opus-doc")}
              />
              <PdfCardOption
                selected={selectedCardOption === "pdf"}
                onClick={() => setSelectedCardOption("pdf")}
              />
            </div>
            <div className="flex items-center justify-between border-t border-input p-4">
              <Text
                variant="fine-print-description"
                className="text-muted-foreground"
              >
                You can change this later from Resource Settings
              </Text>
              <div className="flex flex-1 items-center justify-end gap-2">
                <Button size="sm" variant="outline" onClick={props.onClose}>
                  <Text variant="fine-print-title">Cancel</Text>
                </Button>
                <Button
                  size="sm"
                  onClick={handleOnSave}
                  disabled={!media}
                  loading={createTrainingResourceLoading}
                  loadingText={saveButtonText}
                >
                  <Text variant="fine-print-title">{saveButtonText}</Text>
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

type OpusDocFeature = {
  icon: React.ComponentType<IconProps>;
  text: string;
};

const OPUS_DOC_FEATURE_LIST: OpusDocFeature[] = [
  {
    icon: Phone2Icon,
    text: "Mobile-friendly format",
  },
  {
    icon: PenOutlineIcon,
    text: "Editable for quick changes",
  },
  {
    icon: LanguageIcon,
    text: "Easily translatable into multiple languages",
  },
];

interface CardProps {
  selected: boolean;
  onClick: (option: CardOption) => void;
}

const OpusDocCardOption: React.FC<CardProps> = ({ selected, onClick }) => {
  return (
    <div
      onClick={() => onClick("opus-doc")}
      className={cn(
        "flex h-[320px] max-w-[320px] flex-1 flex-col items-center justify-center gap-[16px] rounded-3xl border-2 border-input bg-background p-3 transition-all shadow-sm cursor-pointer",
        !selected && "hover:bg-gray-1",
        selected && "border-purple-9 bg-purple-1",
      )}
    >
      <div className="flex items-center justify-center self-stretch">
        <OpusDocumentIllustration
          accentClassName="fill-gray-2"
          className="size-[80px]"
        />
      </div>
      <Text variant="compact-title">Convert into Opus Document</Text>
      <div className="flex flex-col gap-[4px] self-stretch">
        {OPUS_DOC_FEATURE_LIST.map((feature) => (
          <div
            key={feature.text}
            className="flex items-center gap-[8px] self-stretch"
          >
            <div className="flex size-8 items-center justify-center rounded-full bg-purple-alpha-1">
              <feature.icon className="size-4 text-purple-9" />
            </div>
            <Text
              variant="fine-print-description"
              className="text-muted-foreground"
            >
              {feature.text}
            </Text>
          </div>
        ))}
      </div>
    </div>
  );
};

const PdfCardOption: React.FC<CardProps> = ({ selected, onClick }) => {
  return (
    <div
      onClick={() => onClick("pdf")}
      className={cn(
        "flex h-[320px] max-w-[320px] flex-1 flex-col items-center justify-center gap-[16px] rounded-3xl border-2 border-input bg-background p-3 transition-all shadow-sm cursor-pointer",
        !selected && "hover:bg-gray-1",
        selected && "border-blue-9 bg-blue-1",
      )}
    >
      <PdfDocumentIllustration className="size-[80px]" />
      <div className="flex flex-col items-center gap-3">
        <Text variant="compact-title">Keep as original PDF</Text>
        <Text
          variant="fine-print-description"
          className="text-center text-muted-foreground"
          multiline
        >
          PDF is non editable, not mobile friendly and not translatable.
        </Text>
      </div>
    </div>
  );
};

gql`
  mutation ConvertPdfToOpusDocDialog_CreateTrainingResource(
    $input: CreateTrainingResourceInput!
  ) {
    createTrainingResourceV2(input: $input) {
      success
      trainingResource {
        ...UploadedTrainingResource
      }
    }
  }
`;

export default ConvertPdfToOpusDocDialog;
